import { Chip, Stack, Typography } from '@mui/material';
import { ListingStatus as LS } from '@prisma/client';
import { CustomTooltip } from '../CustomTooltip/CustomTooltip';
import { darken, lighten, PaletteColor } from '@mui/material/styles';
import { useState } from 'react';
import useIsMobile from '../../hooks/useIsMobile';
import { InfoOutlined } from '@mui/icons-material';

interface Props {
  status: LS | null;
}

const getTextForStatus = (status: LS | null) => {
  switch (status) {
    case 'PENDING':
      return 'Pending';
    case 'APPROVED':
      return 'Approved';
    case 'DRAFT':
      return 'Draft';
    case 'PRICE_PENDING':
      return 'Price Pending';
    case 'AWAITING_SIGNATURE':
      return 'Awaiting Signature';
  }
};

const getTooltipTextForStatus = (status: LS | null) => {
  switch (status) {
    case 'PENDING':
      // eslint-disable-next-line max-len
      return 'The listing is pending approval by Homepoint. Your listing will stay in this status until pricing, photos, and other details are completed.';
    case 'APPROVED':
      return 'The listing is approved by Homepoint.';
    case 'DRAFT':
      return 'The listing is a draft.';
    case 'PRICE_PENDING':
      return 'Waiting for pricing options and approval.';
    case 'AWAITING_SIGNATURE':
      return 'Price approved. Waiting for listing agreement to be signed.';
  }
};

const getColorForStatus = (
  status: LS | null
): 'warning' | 'success' | 'primary' => {
  switch (status) {
    case 'PENDING':
      return 'warning';
    case 'APPROVED':
      return 'success';
    case 'DRAFT':
      return 'primary';
    case 'PRICE_PENDING':
      return 'warning';
    case 'AWAITING_SIGNATURE':
      return 'warning';
    default:
      return 'primary';
  }
};

const ListingStatus: React.FC<Props> = ({ status }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const isMobile = useIsMobile();
  if (!status) return null;

  return (
    <Stack direction="row" spacing={1}>
      <CustomTooltip
        title={
          <Typography variant="body2">
            {getTooltipTextForStatus(status)}
          </Typography>
        }
        open={showTooltip}
        onOpen={() => setShowTooltip(true)}
        onClose={() => setShowTooltip(false)}
        disableTouchListener={isMobile}
        width="300px"
      >
        <Chip
          label={
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="caption" fontWeight={600}>
                {getTextForStatus(status)}
              </Typography>
              <InfoOutlined
                fontSize="small"
                sx={{
                  color: (theme) =>
                    darken(theme.palette[getColorForStatus(status)].main, 0.1),
                }}
              />
            </Stack>
          }
          clickable={false}
          onClick={() => setShowTooltip(!showTooltip)}
          sx={{
            backgroundColor: (theme) => {
              const color = getColorForStatus(status);
              return lighten(
                (
                  theme.palette[
                    color as keyof typeof theme.palette
                  ] as PaletteColor
                ).main,
                0.4
              );
            },
          }}
        />
      </CustomTooltip>
    </Stack>
  );
};

export default ListingStatus;
